import React, { useState, useEffect, useCallback } from 'react';
import { connectionRequestAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Search, UserPlus, CheckCircle, XCircle, Clock, PhoneCall, Smartphone, Globe, Trash2, RotateCcw, Save, Briefcase, Send, Repeat, QrCode, FileText } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

const STATUS_BADGE = { new: 'warning', contacted: 'info', converted: 'resolved', rejected: 'danger', deleted: 'muted' };
const CONNECTION_TYPE_LABEL = { catv: 'CableTV', broadband: 'Broadband', both: 'Both' };
const SOURCE_LABEL = { portal: 'Portal', agent: 'Agent', admin: 'Admin' };

const STATUS_OPTIONS = [
  { status: 'new',       label: 'New' },
  { status: 'contacted', label: 'Contacted' },
  { status: 'converted', label: 'Converted' },
  { status: 'rejected',  label: 'Rejected' },
];

function RequestActionModal({ request, onClose, onSuccess }) {
  const [notes, setNotes] = useState(request.reviewNotes || '');
  const [acting, setActing] = useState(false);
  const isDeleted = request.status === 'deleted';

  const setStatus = async (status) => {
    if (status === request.status) return;
    setActing(true);
    try {
      const { data } = await connectionRequestAPI.updateStatus(request._id, { status, reviewNotes: notes });
      toast.success(data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setActing(false);
    }
  };

  const setCategory = async (connectionType) => {
    if (connectionType === (request.connectionType || '')) return;
    setActing(true);
    try {
      const { data } = await connectionRequestAPI.updateStatus(request._id, { connectionType });
      toast.success(data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update category');
    } finally {
      setActing(false);
    }
  };

  const saveNotes = async () => {
    setActing(true);
    try {
      const { data } = await connectionRequestAPI.updateStatus(request._id, { reviewNotes: notes });
      toast.success(data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save notes');
    } finally {
      setActing(false);
    }
  };

  const deleteLead = async () => {
    if (!window.confirm(`Delete the lead for "${request.name}"? It'll be hidden from the inbox but can be restored later.`)) return;
    setActing(true);
    try {
      const { data } = await connectionRequestAPI.deleteRequest(request._id);
      toast.success(data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete lead');
    } finally {
      setActing(false);
    }
  };

  const restoreLead = async () => {
    setActing(true);
    try {
      const { data } = await connectionRequestAPI.restoreRequest(request._id);
      toast.success(data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to restore lead');
    } finally {
      setActing(false);
    }
  };

  const sendPlans = async () => {
    setActing(true);
    try {
      const { data } = await connectionRequestAPI.sendPlans(request._id);
      toast.success(data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send plans');
    } finally {
      setActing(false);
    }
  };

  const sendQr = async () => {
    setActing(true);
    try {
      const { data } = await connectionRequestAPI.sendQr(request._id);
      toast.success(data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send QR');
    } finally {
      setActing(false);
    }
  };

  const sendDocs = async () => {
    setActing(true);
    try {
      const { data } = await connectionRequestAPI.sendDocs(request._id);
      toast.success(data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send document list');
    } finally {
      setActing(false);
    }
  };

  const toggleFollowUp = async () => {
    setActing(true);
    try {
      const { data } = await connectionRequestAPI.setFollowUpDaily(request._id, !request.followUpDaily);
      toast.success(data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update follow-up');
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3><UserPlus size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Lead</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <table style={{ width: '100%', fontSize: '.85rem', borderCollapse: 'collapse', marginBottom: 16 }}>
            <tbody>
              <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)', width: 100 }}>Name</td><td>{request.name}</td></tr>
              <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Mobile</td>
                <td><a href={`tel:${request.mobile}`} style={{ color: 'var(--primary)' }}>{request.mobile}</a></td></tr>
              <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Source</td>
                <td>{request.source === 'agent'
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Smartphone size={13} /> Agent{request.createdBy?.name ? ` — ${request.createdBy.name}` : ''}</span>
                  : request.source === 'admin'
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Briefcase size={13} /> Admin{request.createdBy?.name ? ` — ${request.createdBy.name}` : ''}</span>
                  : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Globe size={13} /> Portal</span>}</td></tr>
              {!isDeleted && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Category</td>
                  <td>
                    <select className="form-control" style={{ width: 150, padding: '5px 8px', fontSize: '.85rem' }}
                      value={request.connectionType || ''} disabled={acting}
                      onChange={e => setCategory(e.target.value)}>
                      <option value="">Not set</option>
                      <option value="catv">CableTV</option>
                      <option value="broadband">Broadband</option>
                      <option value="both">Both</option>
                    </select>
                  </td></tr>
              )}
              {isDeleted && request.connectionType && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Category</td><td>{CONNECTION_TYPE_LABEL[request.connectionType] || request.connectionType}</td></tr>
              )}
              {(request.society || request.houseNo) && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Society / House No.</td><td>{[request.society, request.houseNo].filter(Boolean).join(', ')}</td></tr>
              )}
              {request.address && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)', verticalAlign: 'top' }}>Address</td><td>{request.address}</td></tr>
              )}
              {request.note && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)', verticalAlign: 'top' }}>Note</td><td>{request.note}</td></tr>
              )}
              {!isDeleted && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Plans Catalog</td>
                  <td>{request.catalogMessageSent ? <span style={{ color: 'var(--success)' }}>Sent</span> : <span style={{ color: 'var(--danger)' }}>Not sent</span>}</td></tr>
              )}
              {!isDeleted && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Follow-up Everyday</td>
                  <td>{request.followUpDaily
                    ? <span style={{ color: 'var(--success)' }}>On — WhatsApp daily{request.lastFollowUpSentAt ? `, last sent ${format(new Date(request.lastFollowUpSentAt), 'dd/MM/yy')}` : ''}</span>
                    : <span style={{ color: 'var(--text-muted)' }}>Off</span>}</td></tr>
              )}
              <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Submitted</td><td>{format(new Date(request.createdAt), 'dd/MM/yy HH:mm')}</td></tr>
              <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Status</td>
                <td><span className={`badge badge-${STATUS_BADGE[request.status]}`} style={{ textTransform: 'capitalize' }}>{request.status}</span></td></tr>
              {request.reviewedBy && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Reviewed By</td><td>{request.reviewedBy?.name || '—'}</td></tr>
              )}
              {isDeleted && (
                <tr><td style={{ padding: '4px 0', color: 'var(--text-muted)' }}>Deleted</td>
                  <td>{request.deletedAt ? format(new Date(request.deletedAt), 'dd/MM/yy HH:mm') : '—'}{request.deletedBy?.name ? ` by ${request.deletedBy.name}` : ''}</td></tr>
              )}
            </tbody>
          </table>

          <div className="form-group">
            <label className="form-label">Notes (optional)</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} style={{ flex: 1 }} />
              <button type="button" className="btn btn-secondary btn-sm" disabled={acting || notes === (request.reviewNotes || '')} onClick={saveNotes} title="Save notes without changing status">
                <Save size={13} /> Save
              </button>
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ flexWrap: 'wrap' }}>
          {isDeleted ? (
            <button type="button" className="btn btn-primary" disabled={acting} onClick={restoreLead}>
              <RotateCcw size={14} /> Restore Lead
            </button>
          ) : (
            <>
              <select className="form-control" style={{ width: 140 }} value={request.status} disabled={acting}
                onChange={e => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map(({ status, label }) => (
                  <option key={status} value={status}>{label}</option>
                ))}
              </select>
              <button type="button" className="btn btn-secondary" disabled={acting} onClick={sendPlans}
                title="WhatsApp the plan catalog to this lead">
                <Send size={14} /> {request.catalogMessageSent ? 'Resend Plans' : 'Send Plans'}
              </button>
              <button type="button" className="btn btn-secondary" disabled={acting} onClick={sendQr}
                title={request.connectionType === 'catv' ? 'WhatsApp the payment QR' : 'WhatsApp the payment QR (2 barcodes for Broadband/Both)'}>
                <QrCode size={14} /> Send QR
              </button>
              <button type="button" className="btn btn-secondary" disabled={acting} onClick={sendDocs}
                title="WhatsApp the documents-needed list for this lead's category">
                <FileText size={14} /> Docs
              </button>
              <button type="button" className={request.followUpDaily ? 'btn btn-primary' : 'btn btn-secondary'} disabled={acting} onClick={toggleFollowUp}
                title="WhatsApp a follow-up to this lead every day (time set in Message Center → Alerts), until turned off">
                <Repeat size={14} /> {request.followUpDaily ? 'Stop Daily Follow-up' : 'Follow-up Everyday'}
              </button>
              <button type="button" className="btn btn-danger" disabled={acting} onClick={deleteLead}>
                <Trash2 size={14} /> Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ConnectionRequestsPage() {
  useDarkReady();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeRequest, setActiveRequest] = useState(null);
  const [actingId, setActingId] = useState(null);
  const [filters, setFilters] = useState({ page: 1, status: '', source: '', search: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit: 20 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const [rd, sd] = await Promise.all([connectionRequestAPI.getAll(params), connectionRequestAPI.getStats()]);
      setRequests(rd.data.data);
      setPagination(rd.data.pagination);
      setStats(sd.data.data);
    } catch { toast.error('Failed to load requests'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Row-level quick actions next to "View" — same status/delete/restore endpoints the
  // detail modal uses, just without opening it first. Notes stay modal-only since there's
  // nowhere to type them inline.
  const quickAction = async (e, request, action) => {
    e.stopPropagation();
    setActingId(request._id);
    try {
      const { data } = action === 'delete' ? await connectionRequestAPI.deleteRequest(request._id)
        : action === 'restore' ? await connectionRequestAPI.restoreRequest(request._id)
        : action === 'send-plans' ? await connectionRequestAPI.sendPlans(request._id)
        : action === 'send-qr' ? await connectionRequestAPI.sendQr(request._id)
        : action === 'send-docs' ? await connectionRequestAPI.sendDocs(request._id)
        : action === 'toggle-follow-up' ? await connectionRequestAPI.setFollowUpDaily(request._id, !request.followUpDaily)
        : typeof action === 'object' ? await connectionRequestAPI.updateStatus(request._id, action)
        : await connectionRequestAPI.updateStatus(request._id, { status: action });
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    } finally {
      setActingId(null);
    }
  };

  // Shared between the desktop table row and the ≤768px stacked card (see index.css's
  // "Responsive Table" block) so the two stay in sync instead of drifting apart.
  const renderCategorySelect = (r) => (
    r.status === 'deleted' ? (
      <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{CONNECTION_TYPE_LABEL[r.connectionType] || '—'}</span>
    ) : (
      <select className="form-control" style={{ width: 110, padding: '5px 8px', fontSize: '.78rem' }}
        value={r.connectionType || ''} disabled={actingId === r._id}
        title="Category"
        onClick={e => e.stopPropagation()}
        onChange={e => quickAction(e, r, { connectionType: e.target.value })}>
        <option value="">—</option>
        <option value="catv">CableTV</option>
        <option value="broadband">Broadband</option>
        <option value="both">Both</option>
      </select>
    )
  );

  const renderActions = (r) => (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      {r.status === 'deleted' ? (
        <button className="btn btn-secondary btn-sm" title="Restore" disabled={actingId === r._id}
          onClick={e => quickAction(e, r, 'restore')}>
          <RotateCcw size={13} />
        </button>
      ) : (
        <>
          <select className="form-control" style={{ width: 110, padding: '5px 8px', fontSize: '.78rem' }}
            value={r.status} disabled={actingId === r._id}
            onClick={e => e.stopPropagation()}
            onChange={e => quickAction(e, r, e.target.value)}>
            {STATUS_OPTIONS.map(({ status, label }) => (
              <option key={status} value={status}>{label}</option>
            ))}
          </select>
          <button className="btn btn-secondary btn-sm" title={r.catalogMessageSent ? 'Resend plan catalog' : 'Send plan catalog'} disabled={actingId === r._id}
            onClick={e => quickAction(e, r, 'send-plans')}>
            <Send size={13} />
          </button>
          <button className="btn btn-secondary btn-sm"
            title={r.connectionType === 'catv' ? 'Send payment QR' : 'Send payment QR (2 barcodes for Broadband/Both)'}
            disabled={actingId === r._id} onClick={e => quickAction(e, r, 'send-qr')}>
            <QrCode size={13} />
          </button>
          <button className="btn btn-secondary btn-sm" title="Send documents-needed list" disabled={actingId === r._id}
            onClick={e => quickAction(e, r, 'send-docs')}>
            <FileText size={13} />
          </button>
          <button className="btn btn-secondary btn-sm" title={r.followUpDaily ? 'Stop daily follow-up' : 'Follow-up Everyday — WhatsApp daily'} disabled={actingId === r._id}
            style={r.followUpDaily ? { background: 'var(--primary)', color: 'var(--c-ffffff)', borderColor: 'var(--primary)' } : undefined}
            onClick={e => quickAction(e, r, 'toggle-follow-up')}>
            <Repeat size={13} />
          </button>
          <button className="btn btn-danger btn-sm" title="Delete" disabled={actingId === r._id}
            onClick={e => { e.stopPropagation(); if (window.confirm(`Delete the lead for "${r.name}"?`)) quickAction(e, r, 'delete'); }}>
            <Trash2 size={13} />
          </button>
        </>
      )}
      <button className="btn btn-secondary btn-sm" onClick={() => setActiveRequest(r)}>View</button>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Leads</h2>
          <p className="text-muted text-small">From the portal's "Get a new connection" form and field agents canvassing door-to-door — {pagination.total || 0} total</p>
        </div>
      </div>

      {stats && (
        <div className="stats-grid mb-6">
          {[
            { label: 'New', value: stats.new, color: 'var(--c-d97706)', bg: 'var(--cb-fef3c7)', icon: Clock },
            { label: 'Contacted', value: stats.contacted, color: 'var(--c-2563eb)', bg: 'var(--cb-dbeafe)', icon: PhoneCall },
            { label: 'Converted', value: stats.converted, color: 'var(--c-059669)', bg: 'var(--cb-d1fae5)', icon: CheckCircle },
            { label: 'Rejected', value: stats.rejected, color: 'var(--c-dc2626)', bg: 'var(--cb-fee2e2)', icon: XCircle },
            { label: 'Deleted', value: stats.deleted, color: 'var(--c-6b7280)', bg: 'var(--cb-f3f4f6)', icon: Trash2 },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-icon" style={{ background: s.bg }}><s.icon size={20} color={s.color} /></div>
              <div className="stat-info"><p>{s.label}</p><h3>{s.value}</h3></div>
            </div>
          ))}
        </div>
      )}

      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search name, mobile..."
            value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
        </div>
        <select className="form-control" style={{ width: 140 }} value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="converted">Converted</option>
          <option value="rejected">Rejected</option>
          <option value="deleted">Deleted</option>
        </select>
        <select className="form-control" style={{ width: 130 }} value={filters.source}
          onChange={e => setFilters(f => ({ ...f, source: e.target.value, page: 1 }))}>
          <option value="">All Sources</option>
          <option value="portal">Portal</option>
          <option value="agent">Agent</option>
        </select>
        <button className="btn btn-secondary" onClick={() => setFilters({ page: 1, status: '', source: '', search: '' })}>Clear</button>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : requests.length === 0 ? (
            <div className="empty-state"><UserPlus size={32} /><h3>No requests found</h3></div>
          ) : (
            <>
              <table className="rtable-desktop">
                <thead>
                  <tr><th>Name</th><th>Mobile</th><th>Category</th><th>Source</th><th>Address</th><th>Status</th><th>Submitted</th><th></th></tr>
                </thead>
                <tbody>
                  {requests.map(r => (
                    <tr key={r._id} style={{ cursor: 'pointer', opacity: r.status === 'deleted' ? 0.55 : 1 }} onClick={() => setActiveRequest(r)}>
                      <td style={{ fontWeight: 500 }}>{r.name}</td>
                      <td style={{ fontSize: '.82rem' }}>{r.mobile}</td>
                      <td onClick={e => e.stopPropagation()}>{renderCategorySelect(r)}</td>
                      <td style={{ fontSize: '.78rem' }}>
                        <span className={`badge badge-${r.source === 'agent' || r.source === 'admin' ? 'info' : 'muted'}`}>{SOURCE_LABEL[r.source] || 'Portal'}</span>
                        {(r.source === 'agent' || r.source === 'admin') && r.createdBy?.name && (
                          <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{r.createdBy.name}</div>
                        )}
                      </td>
                      <td style={{ fontSize: '.82rem', color: 'var(--text-muted)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.address || '—'}</td>
                      <td><span className={`badge badge-${STATUS_BADGE[r.status]}`} style={{ textTransform: 'capitalize' }}>{r.status}</span></td>
                      <td style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{format(new Date(r.createdAt), 'dd/MM/yy HH:mm')}</td>
                      <td onClick={e => e.stopPropagation()}>{renderActions(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="rtable-cards">
                {requests.map(r => (
                  <div key={r._id} className="rtable-card clickable" style={{ opacity: r.status === 'deleted' ? 0.55 : 1 }} onClick={() => setActiveRequest(r)}>
                    <div className="rtable-card-row">
                      <span className="rtable-card-label">Name</span>
                      <span className="rtable-card-value">{r.name}</span>
                    </div>
                    <div className="rtable-card-row">
                      <span className="rtable-card-label">Mobile</span>
                      <span className="rtable-card-value">{r.mobile}</span>
                    </div>
                    <div className="rtable-card-row">
                      <span className="rtable-card-label">Category</span>
                      <span className="rtable-card-value" onClick={e => e.stopPropagation()}>{renderCategorySelect(r)}</span>
                    </div>
                    <div className="rtable-card-row">
                      <span className="rtable-card-label">Source</span>
                      <span className="rtable-card-value">
                        <span className={`badge badge-${r.source === 'agent' || r.source === 'admin' ? 'info' : 'muted'}`}>{SOURCE_LABEL[r.source] || 'Portal'}</span>
                        {(r.source === 'agent' || r.source === 'admin') && r.createdBy?.name && (
                          <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{r.createdBy.name}</div>
                        )}
                      </span>
                    </div>
                    {r.address && (
                      <div className="rtable-card-row">
                        <span className="rtable-card-label">Address</span>
                        <span className="rtable-card-value">{r.address}</span>
                      </div>
                    )}
                    <div className="rtable-card-row">
                      <span className="rtable-card-label">Status</span>
                      <span className="rtable-card-value"><span className={`badge badge-${STATUS_BADGE[r.status]}`} style={{ textTransform: 'capitalize' }}>{r.status}</span></span>
                    </div>
                    <div className="rtable-card-row">
                      <span className="rtable-card-label">Submitted</span>
                      <span className="rtable-card-value">{format(new Date(r.createdAt), 'dd/MM/yy HH:mm')}</span>
                    </div>
                    <div className="rtable-card-row" style={{ justifyContent: 'flex-start' }} onClick={e => e.stopPropagation()}>
                      {renderActions(r)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        {pagination.pages > 1 && (
          <div className="pagination">
            <button className="page-btn" disabled={!pagination.hasPrev} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <span style={{ fontSize: '.8rem', padding: '0 8px' }}>{pagination.page} / {pagination.pages}</span>
            <button className="page-btn" disabled={!pagination.hasNext} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}
      </div>

      {activeRequest && (
        <RequestActionModal request={activeRequest} onClose={() => setActiveRequest(null)}
          onSuccess={() => { setActiveRequest(null); load(); }} />
      )}
    </div>
  );
}
